import type { Connecting, Effect, Held, Scope, Selected } from './connections';

/** The groups a consent shows permissions in, in this order; `other` holds those whose product says no effect */
export type Group = Effect | 'other';

/** What the decision does to one permission */
export type Change = 'add' | 'keep' | 'remove';

/** What the application can do after the decision, permission by permission */
export interface Outcome {
	adds: Scope[];
	keeps: Scope[];
	removes: Scope[];
	/** Everything it can do afterwards, in the consent's order */
	grant: Scope[];
}

/**
 * One consent's decision for one organization (or all of them): what the application asks for now apart
 * from what it already has there, what the person chooses, and what that leaves it able to do.
 *
 * Three sets are kept apart: requested (`asked`, without what it already has), selected (the person's
 * choice of it) and granted (`outcome`). Only permissions whose product says they only read are
 * suggested, and a suggestion is an ordinary, visible choice the person changes like any other;
 * anything that changes, reaches outside Beyond or can cost money is chosen by the person or not at all.
 * Removing something it already has is a separate, explicit choice (`remove`), never the result of
 * unchecking something asked for now.
 */
export class Grant {
	static GROUPS: Group[] = ['read', 'change', 'publish', 'cost', 'other'];

	#asked: Connecting;
	#organization: string | null;

	/** @param organization The organization chosen, `null` for all of the person's */
	constructor(asked: Connecting, organization: string | null) {
		this.#asked = asked;
		this.#organization = organization;
	}

	/** What it already has for the organization chosen */
	get held(): Held | undefined {
		return this.#asked.existing.find(entry => entry.organization === this.#organization);
	}

	/** What it has through its other grants: for all organizations when one is chosen, and for each one otherwise */
	get others(): Held[] {
		return this.#asked.existing.filter(entry => entry.organization !== this.#organization && entry.scopes.length > 0);
	}

	/** What it already has here, in the consent's order */
	get kept(): Scope[] {
		return Grant.ordered(this.held?.scopes ?? []);
	}

	/** What it asks for that it does not already have here: the choice */
	get asked(): Scope[] {
		const had = new Set(this.kept.map(scope => scope.name));
		return Grant.ordered(this.#asked.scopes.filter(scope => !had.has(scope.name)));
	}

	/** What it asks for again that it already has */
	get repeated(): Scope[] {
		const asked = new Set(this.#asked.scopes.map(scope => scope.name));
		return this.kept.filter(scope => asked.has(scope.name));
	}

	/** The permissions chosen for the person when the page opens: those that only read */
	get suggested(): string[] {
		return this.asked.filter(scope => scope.effect === 'read').map(scope => scope.name);
	}

	/** What the application can do after the decision */
	outcome(selected: Iterable<string>, removed: Iterable<string> = []): Outcome {
		const chosen = new Set(selected);
		const gone = new Set(removed);
		const adds = this.asked.filter(scope => chosen.has(scope.name));
		const removes = this.kept.filter(scope => gone.has(scope.name));
		const keeps = this.kept.filter(scope => !gone.has(scope.name));
		return { adds, keeps, removes, grant: Grant.ordered([...keeps, ...adds]) };
	}

	/**
	 * Whether Allow has something to give: at least one permission left, and something chosen, removed, or
	 * asked for again that it already has (an application connecting again gets new tokens for it)
	 */
	ready(selected: Iterable<string>, removed: Iterable<string> = []): boolean {
		const outcome = this.outcome(selected, removed);
		return outcome.grant.length > 0 && (outcome.adds.length > 0 || outcome.removes.length > 0 || this.repeated.length > 0);
	}

	/** What Allow sends: only names of the request, only removals of what it has, and the version reviewed */
	selection(selected: Iterable<string>, removed: Iterable<string> = []): Selected {
		const outcome = this.outcome(selected, removed);
		return { scopes: outcome.adds.map(scope => scope.name), remove: outcome.removes.map(scope => scope.name), basis: this.held?.version ?? null };
	}

	/** The group a permission is shown in */
	static group(scope: Scope): Group {
		return scope.effect ?? 'other';
	}

	/** Permissions grouped in the consent's order, empty groups left out */
	static grouped(scopes: Scope[]): { group: Group; scopes: Scope[] }[] {
		return Grant.GROUPS.map(group => ({ group, scopes: scopes.filter(scope => Grant.group(scope) === group) })).filter(entry => entry.scopes.length > 0);
	}

	/** By group, keeping each product's own order inside a group */
	static ordered(scopes: Scope[]): Scope[] {
		return Grant.grouped(scopes).flatMap(entry => entry.scopes);
	}
}
